import {useMutateRepository} from '@nfq/typed-next-api';

import {applicationFormService} from 'Application/services/ApplicationFormService';

/**
 * Custom hook for sending contact form data using the contact form service.
 * This hook provides a convenient interface for triggering contact form submissions with automatic error handling.
 * It utilizes the typed Next.js API pattern to ensure type safety throughout the mutation process.
 *
 * @param id The unique identifier for the contact form instance.
 * @returns An object containing the sendOptin function for triggering contact form submissions.
 *
 * @example
 * ```tsx
 * const ContactForm = () => {
 *     const {sendOptin} = useSendContactForm();
 *
 *     const handleSubmit = async (formData: ContactFormData) => {
 *         try {
 *             await sendOptin(formData);
 *             console.log('Contact form sent successfully');
 *         } catch (error) {
 *             console.error('Failed to send contact form:', error);
 *         }
 *     };
 *
 *     return <form onSubmit={handleSubmit}>...</form>;
 * };
 * ```
 */
export const useSendApplicationForm = (id: string) => {
    const {error, trigger} = useMutateRepository<typeof applicationFormService.sendApplicationForm>(
        `sendApplicationForm-${id}`,
        applicationFormService.sendApplicationForm,
        {revalidate: false}
    );

    return {
        error,
        sendApplicationForm: trigger
    };
};